// The staff Pitch Match draws on (issue #3518): where a note sits, which letters label the lines
// and spaces, which ledger lines a note needs, where the stem points, and where the singer's dot
// goes. Pure numbers and plain data - no React, no DOM, no audio - so the staff component and its
// stories stay dumb renderers and the arithmetic can be tested on its own.
//
// Everything is a diatonic step index. The bottom staff line is step 0 (E4 in treble, G2 in bass)
// and every line or space above it is one step more, so the five lines are the even steps 0, 2, 4,
// 6 and 8 and the four spaces the odd steps 1, 3, 5 and 7. A step is a letter, not a semitone: it
// counts lines and spaces the way an eye reads them, which is what the drawing needs. The clef is
// the singer's, from clefForVoicePart: treble for soprano and alto (lines E G B D F, spaces F A C
// E), bass for tenor and bass (lines G B D F A, spaces A C E G). No clef sign is drawn - the
// letters at the left are the key (issue #3338, story 12).
//
// The singer's dot is deliberately not a note. Its position is the cents distance from the target,
// one semitone to the step, so a semitone off is one step off the target (line to adjacent space)
// and 50 cents is half a step, sliding continuously while they search. That keeps the gap between
// "you" and "target" honest where the singer actually looks, and gives up letter accuracy far from
// the target (a fifth away the dot is nowhere near that note's own line); nothing asks the dot to
// name a note, and the clef's drawable band clamps it.
//
// Ledger lines belong to the note, the way a musician reads them: each is a short line centred on
// the notehead, a little wider than the head itself, so two of them read as two ledger lines and
// not as a seven-line staff. Their letters travel with them - a ledger letter, and the space letter
// of a target that sits outside the staff, are drawn just left of the short lines rather than in
// the staff's left-hand letter columns.
//
// The layout numbers are the two "Pitch Match, staff version" artboards, which is why they are
// concrete pixels inside a 350-wide SVG rather than proportions; every vertical one comes from
// stepHeight, half the 22 px gap between staff lines.

import { Clef } from '../../../../../common/src/learn_rules.ts';
import { VocalRange } from '../../../../../common/src/model.ts';

// ---- Steps, notes and letters ----------------------------------------------------------------

const letterForDegree = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

// The diatonic degree each pitch class is drawn at. A note that is not a natural takes the step of
// the natural below it, which matches the app's flat note names (Bb is drawn where A is, with the
// flat that would tell them apart left off). Easy picks natural notes only, so no accidental is
// ever drawn in this exercise; this is only what a stray non-natural falls back to.
const degreeForPitchClass = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6];

// The natural each diatonic degree names, from C: diatonicIndex read backwards
const semitoneForDegree = [0, 2, 4, 5, 7, 9, 11];

// Lines and spaces counted from C0, so that steps subtract cleanly between any two notes
function diatonicIndex(midi: number): number {
  const rounded = Math.round(midi);
  return Math.floor(rounded / 12) * 7 + degreeForPitchClass[((rounded % 12) + 12) % 12];
}

function midiForDiatonicIndex(index: number): number {
  return Math.floor(index / 7) * 12 + semitoneForDegree[((index % 7) + 7) % 7];
}

const bottomLineIndex: Record<Clef, number> = {
  treble: diatonicIndex(64), // E4
  bass: diatonicIndex(43), // G2
};

// The step a note is drawn at: 0 is the bottom line, negative steps are below the staff
export function staffStepForMidi(midi: number, clef: Clef): number {
  return diatonicIndex(midi) - bottomLineIndex[clef];
}

// The natural a step names, as a midi number: staffStepForMidi read backwards
export function midiForStaffStep(step: number, clef: Clef): number {
  return midiForDiatonicIndex(bottomLineIndex[clef] + step);
}

// The letter name of any step, including steps beyond the five lines
export function stepLetter(step: number, clef: Clef): string {
  return letterForDegree[(((bottomLineIndex[clef] + step) % 7) + 7) % 7];
}

// Even steps are lines, odd steps are the spaces between them
export function isLineStep(step: number): boolean {
  return ((step % 2) + 2) % 2 === 0;
}

// The ledger lines a note at `step` needs, ascending: every other step from just outside the staff
// out to the note. Nothing for a note on the staff, and nothing for the space just outside it
// (steps -1 and 9), which needs no line to be read.
export function ledgerStepsFor(step: number): number[] {
  const steps: number[] = [];
  if (step <= -2) {
    for (let ledger = Math.ceil(step / 2) * 2; ledger <= -2; ledger += 2) {
      steps.push(ledger);
    }
    return steps;
  }
  for (let ledger = 10; ledger <= Math.floor(step / 2) * 2; ledger += 2) {
    steps.push(ledger);
  }
  return steps;
}

// Stems point up below the middle line (step 4) and down from it, as they do in printed music
export function stemDirection(step: number): 'up' | 'down' {
  return step < 4 ? 'up' : 'down';
}

// ---- Layout ----------------------------------------------------------------------------------

const stepHeight = 11; // half the 22 px gap between staff lines: one line or space per step
const topMargin = stepHeight * 4; // room above the band for the dot's halo (the treble top line lands at y 110, as drawn)
const bottomMargin = stepHeight * 2;
const haloRadius = 21;
const captionOffset = stepHeight * 4; // "target" and "you" sit this far below the bottom line
const targetX = 165; // centre of the target notehead
const ledgerHalfWidth = 22; // a ledger line reaches this far each side of the notehead (rx 13)

export interface StaffBand {
  minStep: number;
  maxStep: number;
}

// The steps each clef draws, chosen for the voices that read it: treble F3 to E6 (an alto's G3 sits
// at -5, a soprano's C6 at 12) and bass C2 to B4 (a bass's E2 at -2, a tenor's A4 at 15). The bass
// band is two steps higher than the treble one so the tenor's top notes are on it at all. Both are
// twenty steps tall, so one SVG height serves either clef; the bass staff simply sits 22 px lower
// inside it.
const bands: Record<Clef, StaffBand> = {
  treble: { minStep: -6, maxStep: 14 },
  bass: { minStep: -4, maxStep: 16 },
};

export function bandFor(clef: Clef): StaffBand {
  return bands[clef];
}

const bandSteps = bands.treble.maxStep - bands.treble.minStep; // the same for both clefs, by design

// The band's ends as midi notes: every note this clef can draw. The page intersects the singer's
// saved range with it, so a target the staff would have to clamp is never asked for.
export function drawableMidiRange(clef: Clef): VocalRange {
  const { minStep, maxStep } = bands[clef];
  return { lowMidi: midiForStaffStep(minStep, clef), highMidi: midiForStaffStep(maxStep, clef) };
}

// Where a step sits inside the SVG: the top of the clef's band is at the top margin and y grows
// downwards as steps fall.
export function stepY(step: number, clef: Clef): number {
  return topMargin + (bands[clef].maxStep - step) * stepHeight;
}

// The baseline of the "target" and "you" captions for a target at `targetStep`: below the bottom
// line, or below the target's own ledger lines when it sits under the staff, so a low note never
// runs into its caption. The captions move only between a note on the staff and one under it.
export function captionYFor(targetStep: number, clef: Clef): number {
  return stepY(Math.min(0, targetStep), clef) + captionOffset;
}

export const staffLayout = {
  stepHeight,
  width: 350,
  // the captions under the lowest drawable target, then a margin; both bands are bandSteps tall, so
  // this one height draws either clef
  height: topMargin + bandSteps * stepHeight + captionOffset + bottomMargin,
  lineX1: 10,
  lineX2: 340,
  lineWidth: 1.4,
  lineLetterX: 31, // centre of the grey line letters on their white tabs
  spaceLetterX: 58, // centre of the purple space letters
  // centre of a letter that travels with the note - a ledger letter, or the space letter of a
  // target outside the staff - just left of the short ledger lines and clear of the notehead
  ledgerLetterX: targetX - 34,
  letterFontSize: 14,
  tabSize: 18, // the white tab a line letter sits on
  tabCornerRadius: 4,
  targetX,
  noteheadRx: 13,
  noteheadRy: 9.5,
  noteheadRotation: -20, // degrees, about the notehead's centre
  stemOffsetX: 11.5, // from the notehead's centre to its right edge, where the stem stands
  stemFootOffsetY: 3, // the stem starts this far from the notehead's centre, inside the head
  stemLength: 64, // from the notehead's centre to the stem's end
  stemWidth: 2.4,
  // A ledger line is a short line centred on the notehead, as printed music draws it
  ledgerHalfWidth,
  guideX1: 185,
  guideX2: 330,
  guideDash: '4 4',
  guideOpacity: 0.6,
  dotX: 285, // the "you" column
  dotRadius: 15,
  dotStrokeWidth: 3, // white, so the dot reads over a staff line
  haloRadius,
  haloStrokeWidth: 2,
  haloOpacity: 0.5,
  captionFontSize: 13,
  captionOffset,
};

// Where the singer's dot sits, as a continuous step: 100 cents from the target is one step, so a
// semitone off is a line away from the target's space (and 50 cents is half of that), clamped to
// the clef's drawable band. Relative to the target by design - see the note at the top of this file.
export function dotStep(targetStep: number, cents: number, clef: Clef): number {
  const { minStep, maxStep } = bands[clef];
  return Math.max(minStep, Math.min(maxStep, targetStep + cents / 100));
}

// ---- Letters to draw -------------------------------------------------------------------------

// 'line' and 'space' are the staff's own letters (grey on a white tab, and purple); 'ledger' is a
// letter for a ledger line the target needs, drawn like a line letter but beside the short ledger
// lines, at ledgerLetterX - as is a 'space' letter for a target outside the staff.
export type StaffLetterKind = 'line' | 'space' | 'ledger';

export interface StaffLetter {
  step: number;
  letter: string;
  kind: StaffLetterKind;
}

// Every letter the staff shows for one target, ascending by step: the five line letters, the four
// space letters, a letter for each of the target's ledger lines, and - when the target sits in a
// space beyond the staff - that space's letter, so the note the singer is aiming at is always
// named.
export function staffLetters(clef: Clef, targetStep: number): StaffLetter[] {
  const letters: StaffLetter[] = [];
  for (let step = 0; step <= 8; step += 1) {
    letters.push({ step, letter: stepLetter(step, clef), kind: isLineStep(step) ? 'line' : 'space' });
  }
  for (const step of ledgerStepsFor(targetStep)) {
    letters.push({ step, letter: stepLetter(step, clef), kind: 'ledger' });
  }
  if (!isLineStep(targetStep) && (targetStep < 0 || targetStep > 8)) {
    letters.push({ step: targetStep, letter: stepLetter(targetStep, clef), kind: 'space' });
  }
  return letters.sort((first, second) => first.step - second.step);
}
